import * as Actions from './actions/actions'
import { isFullScreenWidth } from '../../../util/detect'
import { settings } from '../../../settings/settings-util'
import reducer from './reducers/index'
import Renderer from './renderers/renderer'
import Mutator from './renderers/mutator'

import watch from 'redux-watch'

export default class DragWindow {
    constructor(passedSettings) {
        this._settings = passedSettings || settings;
        this._renderer = new Renderer(this._settings);
        this._mutator = new Mutator(this._settings);

        try {
            this._$ = require('jquery').noConflict(true);
        } catch (e) {
            console.error(`DragWindow error: ${e}`)
        }
    }

    static getDefaultSettings() {
        return settings
    }

    getReducer() {
        return reducer;
    }

    getRenderer() {
        return this._renderer.getRender();
    }

    getMutator() {
        return this._mutator.getMutate();
    }

    async initialize(store, initializationPromises) {
        let self = this;

        this._store = store;

        this._renderer.initialize(store);
        this._mutator.initialize(store);

        this._$(window).on('resize scroll', (e) => {
            // Bail if event not from window
            if (e && e.target !== window) {
                return;
            }

            // Check whether in mobile (full-screen) mode.
            // If so, bail.
            const { windowClassName } = this._settings.classNames;
            const element = document.getElementsByClassName(windowClassName);
            if (isFullScreenWidth(element[0]) === false) {
                this.positionInViewport();
            }
        });

        await initializationPromises?.onLoad;
        this.positionInViewport();
    }

    getVertScrollBarWidth(){
        if(document.body){
            return window.innerWidth - document.body.scrollWidth;
        }
        else{
            return 0;
        }
    }

    fixedAgentPosition($agentWindow) {
        return {
            top: parseInt($agentWindow.css('top')),
            left: parseInt($agentWindow.css('left')),
            height: parseInt($agentWindow.css('height'), 10),
            width: parseInt($agentWindow.css('width'), 10),
        }
    }

    fixedSafePosition(gutterPixels, win) {
        return {
            top: gutterPixels,
            left: gutterPixels,
            right: (win.width - (gutterPixels + this.getVertScrollBarWidth())),
            bottom: (win.height - gutterPixels),
        }
    }

    absoluteAgentPosition($agentWindow) {
        return {
            top: $agentWindow.position().top,
            left: $agentWindow.position().left,
            height: parseInt($agentWindow.css('height'), 10),
            width: parseInt($agentWindow.css('width'), 10),
        }
    }

    absoluteSafePosition($window, gutterPixels, win) {
        return {
            top: $window.scrollTop() + gutterPixels,
            left: $window.scrollLeft() + gutterPixels,
            right: $window.scrollLeft() + (win.width - (gutterPixels + this.getVertScrollBarWidth())),
            bottom: $window.scrollTop() + (win.height - gutterPixels),
        }
    }

    stateAgentPosition(state) {
        const sLoc = this._settings.stateLocations;
        const { height, width } = 
            state[sLoc.Window.Base][sLoc.Window.size];
        const { xPos, yPos } = 
            state[sLoc.Window.Base][sLoc.Window.position];

        return {
            top: parseInt(yPos, 10),
            left: parseInt(xPos, 10),
            height: parseInt(height, 10),
            width: parseInt(width, 10),
        };
    }

    slideIntoView($agentWindow, destination, dispatchMoveAction) {
        let animating = !!this._$(':animated').has($agentWindow[0]).length;
        let target = animating ? $agentWindow : $agentWindow.stop();
        target.animate({ top: destination.top, left: destination.left }, 'slow', 'linear', function() {
            dispatchMoveAction(destination);
        });
    }

    getNewPosition(current, safe, agent, win, gutterPixels, hugHorizontal) {
        let newPos = {
            left: current.left,
            top: current.top
        };

        // check if current position is outside the viewport
        if (current.top < safe.top)
            newPos.top = safe.top;
        else if (current.bottom > safe.bottom)
            newPos.top = safe.bottom - agent.height;

        if (current.left < safe.left)
            newPos.left = safe.left;
        else if (current.right > safe.right)
            newPos.left = safe.right - agent.width;

        // check if window is smaller than the agent
        if (win.height <= agent.height)
            newPos.top = safe.top - gutterPixels;
        if (win.width <= agent.width)
            newPos.left = hugHorizontal === "left" ? safe.left - gutterPixels : win.width - (agent.width + gutterPixels);

        return newPos;
    }

    positionInViewport() {
        let _this = this;
        let windowClassName = '.' + _this._settings.classNames.windowClassName;
        let gutterPixels = _this._settings.Window.gutterSize != null ? 
            _this._settings.Window.gutterSize :
            15;
        let win = {
            height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
            width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
        };   
        let hugHorizontal = _this._settings.Window.hugHorizontal || "left";

        let $agentWindow = _this._$(windowClassName);
        const updateStateOnly = !$agentWindow || !$agentWindow.length;

        let agent = {}, safe = {}, current = {};
        if (updateStateOnly) {
            agent = _this.stateAgentPosition(_this._store.getState());
            // TODO: DOM is separate from settings/state. Need to find a way to 
            //       determine which calculation to use based on settings.
            //       Assuming fixed for now since absolute is untested.
            safe = _this.fixedSafePosition(gutterPixels, win);
        } else if ($agentWindow.css('position') === 'fixed') {
            agent = _this.fixedAgentPosition($agentWindow);
            safe = _this.fixedSafePosition(gutterPixels, win);
        } else {
            //todo: haven't done any testing with absolute positioning yet
            let $window = _this._$(window);
            agent = _this.absoluteAgentPosition($agentWindow);
            safe = _this.absoluteSafePosition($window, gutterPixels, win);
        }
        current.top = agent.top;
        current.left = agent.left;
        current.right = agent.left + agent.width;
        current.bottom = agent.top + agent.height;

        // set current position
        const newPos = _this.getNewPosition(current, safe, agent, win, gutterPixels, hugHorizontal);

        // reposition the agent if needed
        //if (newPos.top !== current.top || newPos.left !== current.left) {
        if (newPos.top === current.top && newPos.left === current.left) {
            return;
        }

        const dispatchMoveAction = (newPos) => {
            let positionStateLoc = _this._settings.stateLocations.Window.position;
            _this._store.dispatch(Actions.setWindowPosition({
                xPos: newPos.left,
                yPos: newPos.top
            }, positionStateLoc));
        };

        if (updateStateOnly) {
            dispatchMoveAction(newPos);
        } else {
            _this.slideIntoView($agentWindow, newPos, dispatchMoveAction);
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/window-jquery-drag/src/window-jquery-drag.js